// Chooses the SessionStore implementation for the current environment.
//
// This is the single swap point between the local (browser) history used in
// local-agent dev and the server-backed history used in the deployed build.
// Everything above it (the `useSessions` hook, the sidebar) depends only on the
// `SessionStore` interface, so the choice lives here and nowhere else.

import { LOCAL_AGENT } from "@/lib/localDev"
import { createLocalSessionStore, type SessionStore } from "./sessionStore"
import { createRemoteSessionStore } from "./remoteSessionStore"

/** Inputs needed to build a store for either environment. */
export interface CreateSessionStoreDeps {
  /** Per-user key for namespacing local history (ignored by the remote store). */
  ownerKey: string
  /** Current Cognito ID token getter (used by the remote store). */
  getIdToken: () => string | undefined
  /**
   * Whether the deployed sessions REST API exists. When false (the default),
   * the deployed build falls back to browser-local history instead of calling
   * a non-existent `/sessions` route. Flip to true once the route is deployed
   * (via `sessionsApiEnabled` in aws-exports.json). Ignored in local-agent mode.
   */
  remoteEnabled?: boolean
}

/**
 * Build the appropriate `SessionStore`.
 *
 * Uses the browser-local store in local-agent mode, and in the deployed build
 * whenever the sessions API isn't available yet (`remoteEnabled` false). Uses
 * the server-backed store only when deployed AND the route exists.
 *
 * @param deps - owner key, ID-token getter, and the remote-enabled flag.
 * @returns the chosen `SessionStore`.
 */
export function createSessionStore(deps: CreateSessionStoreDeps): SessionStore {
  if (LOCAL_AGENT || !deps.remoteEnabled) {
    return createLocalSessionStore(deps.ownerKey)
  }
  return createRemoteSessionStore({ getIdToken: deps.getIdToken })
}
